import { ChangeEvent, useContext, useEffect, useState } from 'react'
import { CircularProgress, Divider, SelectChangeEvent } from '@mui/material'
import CustomPaper from '../../containers/Paper'
import CustomInput from '../../components/TextInput'
import CustomButton from '../../components/Button'
import {
    containsLowercase,
    containsNotBlankSpace,
    containsNumber,
    containsSpecialCharacter,
    containsUppercase,
    validateEmail,
    validatePassword,
} from '../../helpers/utils'
import { RegisterContext } from '../../context/RegisterContext'
import { RegisterFormInfoTypes } from '../../helpers/enums'
import AuthService from '../../services/AuthService'
import { useSnackbar } from 'notistack'
import { VerifyExistenceResponse } from '../../models/auth'
import { UserDTO } from '../../models/users'
import { Tooltip } from 'react-tooltip'
import { CheckRules, DEFAULT_CHECKS, PasswordTooltip } from '../../components/Tooltip'
import CustomDropdown from '../../components/Dropdown'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { TRegisterInfo } from '../../hooks/useFormRegister'

const DEFAULT_ERRORS = {
    email: '',
    password: '',
    repeatPassword: '',
    dni: '',
    documentType: '',
    urlUserLinkedIn: '',
}

interface Props {
    handleContinue: () => void
    linkedInUser?: UserDTO
}

const RegisterForm = ({ handleContinue, linkedInUser }: Props) => {
    const [pending, setPending] = useState<boolean>(false)
    const [errors, setErrors] = useState(DEFAULT_ERRORS)
    const { registerInfo, saveInfo } = useContext(RegisterContext)
    const [show, setShow] = useState<{ password: boolean; repeteadPassword: boolean }>({
        password: false,
        repeteadPassword: false,
    })
    const { enqueueSnackbar } = useSnackbar()
    const [checkedRules, setCheckedRules] = useState<CheckRules>(DEFAULT_CHECKS)

    const handleCheckPassword = (password: string) => {
        setCheckedRules((prev) => ({
            ...prev,
            lowerCase: containsLowercase(password),
            upperCase: containsUppercase(password),
            number: containsNumber(password),
            specialCharacter: containsSpecialCharacter(password),
            long: password.length >= 8,
            notBlankSpace: containsNotBlankSpace(password),
        }))
    }

    const handleOnChangePassword = (
        event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string | Array<string>>,
    ) => {
        handleCheckPassword(event.target.value as string)
        const newInfo = { ...registerInfo, password: event.target.value }
        saveInfo(RegisterFormInfoTypes.Register, newInfo)
    }

    const handleOnChangeRepeatPassword = (
        event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string | Array<string>>,
    ) => {
        setCheckedRules((prev) => ({
            ...prev,
            samePassword: event.target.value === registerInfo.password,
        }))
        const newInfo = { ...registerInfo, repeatPassword: event.target.value }
        saveInfo(RegisterFormInfoTypes.Register, newInfo)
    }

    const handleOnChange = (
        event: ChangeEvent<HTMLInputElement> | SelectChangeEvent<string | Array<string>>,
    ) => {
        const newInfo = { ...registerInfo, [event.target.name]: event.target.value }
        saveInfo(RegisterFormInfoTypes.Register, newInfo)
    }

    const handleButtonDisabled = () => {
        const requiredFields: (keyof TRegisterInfo)[] = linkedInUser
            ? ['email', 'documentValue', 'documentType', 'urlUserLinkedIn']
            : ['email', 'documentValue', 'documentType', 'password', 'repeatPassword']

        const emptyField = requiredFields.some((field) => registerInfo[field] === '')

        return emptyField || pending
    }

    useEffect(() => {
        if (linkedInUser) {
            const newInfo = {
                ...registerInfo,
                email: linkedInUser.email,
            }
            saveInfo(RegisterFormInfoTypes.Register, newInfo)
        }
    }, [linkedInUser, saveInfo, registerInfo])

    const validateInfo = () => {
        const newErrors = {
            email: validateEmail(registerInfo.email) ? '' : 'El email ingresado no es válido.',
            password:
                !linkedInUser && !validatePassword(registerInfo.password)
                    ? 'La contraseña no es válida'
                    : '',
            repeatPassword:
                registerInfo.password === registerInfo.repeatPassword
                    ? ''
                    : 'Las contraseñas no coinciden.',
            dni: '',
            documentType:
                registerInfo.documentType === '' ? 'Falta seleccionar el tipo de documento' : '',
            urlUserLinkedIn:
                !!registerInfo.urlUserLinkedIn &&
                !registerInfo.urlUserLinkedIn.includes('linkedin.com')
                    ? 'El link no pertenece a LinkedIn'
                    : '',
        }

        setErrors(newErrors)
        return Object.values(newErrors).every((x) => x === '')
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return

        setPending(true)
        const authResponse = await new AuthService().verifyExistence(registerInfo)
        setPending(false)

        if (authResponse.status !== 200) {
            enqueueSnackbar('Error en la solicitud.', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        } else {
            const data: VerifyExistenceResponse = authResponse.data
            const newErrors = {
                password: '',
                repeatPassword: '',
                dni: !data.dniAvailable ? 'Ya existe una cuenta asociada al DNI ingresado' : '',
                documentType: '',
                email:
                    !data.emailAvailable && !linkedInUser
                        ? 'Ya existe una cuenta asociada al email ingresado'
                        : '',
                urlUserLinkedIn: '',
            }
            setErrors(newErrors)
            if (Object.values(newErrors).every((x) => x === '')) {
                handleContinue()
            }
        }
    }

    return (
        <>
            <CustomPaper className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[450px] py-3 md:py-6">
                <div className="flex items-center justify-center gap-3 mb-4 md:gap-6 px-2 md:mb-6 w-[85%]">
                    <img
                        src="/logo.png"
                        alt="logo"
                        className="h-[45px] md:h-[60px]"
                    />
                    <span className="text-primary font-semibold text-base md:text-xl">
                        Sistema de seguimiento de graduados
                    </span>
                </div>
                <span className="text-secondary text-base md:text-xl">Registro de usuario</span>
                <Divider />
                <div className="flex flex-col gap-3 mt-4 md:gap-4 md:mt-6">
                    <CustomInput
                        type="text"
                        label="Email"
                        placeholder="Ingresá tu email"
                        name="email"
                        value={registerInfo.email}
                        onChange={(e) => handleOnChange(e as ChangeEvent<HTMLInputElement>)}
                        errorText={errors.email}
                        disabled={Boolean(linkedInUser)}
                    />
                    <CustomDropdown
                        label="Tipo de Documento"
                        name="documentType"
                        data={[
                            'DNI',
                            'Pasaporte',
                            'Cédula de Identidad',
                            'Libreta de Enrolamiento',
                            'Libreta Cívica',
                            'Otro',
                        ]}
                        value={registerInfo.documentType}
                        onChange={(e) => handleOnChange(e)}
                        fullHeight
                    />
                    <CustomInput
                        type="text"
                        label="Número de Documento"
                        placeholder="Ingresá tu Número de Documento"
                        name="documentValue"
                        value={registerInfo.documentValue}
                        onChange={(e) => handleOnChange(e as ChangeEvent<HTMLInputElement>)}
                        errorText={errors.dni}
                    />
                    <CustomInput
                        type={'text'}
                        label="LinkedIn"
                        placeholder="Ingresá el link a tu perfil de LinkedIn"
                        name="urlUserLinkedIn"
                        value={registerInfo.urlUserLinkedIn}
                        onChange={(e) => handleOnChange(e as ChangeEvent<HTMLInputElement>)}
                        errorText={errors.urlUserLinkedIn}
                    />
                    {!linkedInUser && (
                        <>
                            <CustomInput
                                type={show.password ? 'text' : 'password'}
                                label="Contraseña"
                                placeholder="Ingresá tu contraseña"
                                name="password"
                                value={registerInfo.password}
                                onChange={(e) =>
                                    handleOnChangePassword(e as ChangeEvent<HTMLInputElement>)
                                }
                                errorText={errors.password}
                                className="tooltip"
                                iconButtons={[
                                    {
                                        icon: show.password ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        ),
                                        action: () =>
                                            setShow((prev) => ({
                                                ...prev,
                                                password: !prev.password,
                                            })),
                                    },
                                ]}
                            />
                            <CustomInput
                                type={show.repeteadPassword ? 'text' : 'password'}
                                label="Repetir contraseña"
                                placeholder="Repetí tu contraseña"
                                name="repeatPassword"
                                value={registerInfo.repeatPassword}
                                onChange={(e) =>
                                    handleOnChangeRepeatPassword(e as ChangeEvent<HTMLInputElement>)
                                }
                                errorText={errors.repeatPassword}
                                className="tooltip"
                                iconButtons={[
                                    {
                                        icon: show.repeteadPassword ? (
                                            <VisibilityIcon />
                                        ) : (
                                            <VisibilityOffIcon />
                                        ),
                                        action: () =>
                                            setShow((prev) => ({
                                                ...prev,
                                                repeteadPassword: !prev.repeteadPassword,
                                            })),
                                    },
                                ]}
                            />
                        </>
                    )}
                </div>
                <CustomButton
                    className="w-full mt-3 md:mt-6 h-[36px]"
                    onClick={handleSubmit}
                    disabled={handleButtonDisabled()}
                >
                    {!pending ? (
                        'Continuar'
                    ) : (
                        <CircularProgress
                            className="text-primary"
                            size={24}
                        />
                    )}
                </CustomButton>
            </CustomPaper>
            <Tooltip
                anchorSelect=".tooltip"
                place="right"
                className="shadow-custom bg-secondary text-white rounded-md w-[265px]"
            >
                <PasswordTooltip checked={checkedRules} />
            </Tooltip>
        </>
    )
}

export default RegisterForm
